import React, { useCallback, useContext, useEffect, useState } from "react";
import { CoreCard, CoreModal, CoreTextBox } from "@verity/components/core";
import ProctoringPlatform from "../../../../images/settings/proctoring-platform.svg?react";
import ProctoringSettings from "../../../../images/settings/proctoring-settings.svg?react";
import ProfileName from "../../../../images/settings/profile-name.svg?react";
import i18n from "../../../../i18n";
import {
	Application,
	Extension,
	OptionRelatedInstituteSettings,
	ProctoringOption,
	ProctoringOptionProfileV2,
	ProctoringPlatforms,
} from "@verity/proctoring-option/types";
import {
	getProctoringOptionDisabledDetails,
	isAnyOnValueEnabledForProctoringOption,
	isOptionSupportedForPlatform,
	PROCTORING_OPTION_INFO,
} from "@verity/proctoring-option";
import {
	VerityInstituteSettings,
	getDefaultProctoringOption,
	PROCTORING_OPTION_CATEGORIES,
	getOptionsInCategory,
	ModeOptions,
	ProctoringOptionsList,
	shouldShowProctoringOption,
} from "@verity/components";
import apiClient from "../../../../helpers/api-client";
import { Trans } from "react-i18next";
import { PortalControllerContext } from "../../../context";

export const EditProfileModal = ({
	profile,
	editProfile,
	verityInstituteSettings,
	instituteSettings,
	open,
	onClose,
}: {
	profile: ProctoringOptionProfileV2;
	editProfile: (profile: ProctoringOptionProfileV2) => void;
	verityInstituteSettings: VerityInstituteSettings;
	instituteSettings: OptionRelatedInstituteSettings;
	open: boolean;
	onClose: () => void;
}) => {
	const { portalContext } = useContext(PortalControllerContext);
	const [currProfile, setCurrProfile] = React.useState(profile);
	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);

	const updateProfileOption = (proctoringOption: ProctoringOption) => {
		setCurrProfile({ ...currProfile, proctoringOption });
	};

	useEffect(() => {
		async function fetchApplicationData() {
			const APPLICATION_LIST = await apiClient.verityOptions.getAllApplications();
			setApplications(APPLICATION_LIST);
		}

		async function fetchExtensionData() {
			const EXTENSION_LIST = await apiClient.verityOptions.getAllExtensions();
			setExtensions(EXTENSION_LIST);
		}

		void fetchApplicationData();
		void fetchExtensionData();
	}, []);

	const proctoringOptions = currProfile.proctoringOption;
	const shouldShowOption = useCallback(
		(option: keyof ProctoringOption) =>
			shouldShowProctoringOption(option, proctoringOptions, verityInstituteSettings, instituteSettings),
		[proctoringOptions, verityInstituteSettings, instituteSettings],
	);
	const isOptionAllowed = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) =>
			value === undefined
				? isAnyOnValueEnabledForProctoringOption(option, proctoringOptions, verityInstituteSettings, instituteSettings)
				: !getProctoringOptionDisabledDetails(
						option,
						value,
						proctoringOptions,
						verityInstituteSettings,
						instituteSettings,
					).disabled,
		[proctoringOptions, verityInstituteSettings, instituteSettings],
	);

	const getPartiallySupportedPlatforms = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]): Set<keyof ProctoringPlatforms> => {
			const optionInfo = PROCTORING_OPTION_INFO[option];

			const valuesToCheck =
				value !== undefined
					? [value]
					: optionInfo?.isList
						? [[""] as ProctoringOption[O]]
						: (optionInfo?.onValues ?? []);

			const supportedPlatforms = new Set<keyof ProctoringPlatforms>();
			if (!proctoringOptions) return new Set();
			const selectedPlatforms = (["isExtension", "isLockdown", "isMobile"] as (keyof ProctoringPlatforms)[]).filter(
				(platform) => proctoringOptions[platform],
			);

			selectedPlatforms.forEach((platform) => {
				const isSupported = valuesToCheck.some((v) => isOptionSupportedForPlatform(platform, option, v));
				if (isSupported) {
					supportedPlatforms.add(platform);
				}
			});

			// If all selected platforms are supported, return empty
			if (supportedPlatforms.size === selectedPlatforms.length) return new Set();
			return new Set(supportedPlatforms);
		},
		[proctoringOptions],
	);

	const allowedCategories = PROCTORING_OPTION_CATEGORIES.filter((category) =>
		getOptionsInCategory(category).some(shouldShowOption),
	);

	return (
		<CoreModal
			open={open}
			onClose={onClose}
			title={`${i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.edit")}${
				profile.name
			}${i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.setting")}`}
			minWidth="min(80%, 1200px)"
			minHeight="90%"
			widthFixed
			heightFixed
			padding="18px 24px 12px 24px"
			buttons={[
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.update"),
					color: "primary",
					onClick: () => {
						editProfile(currProfile);
						onClose();
					},
				},
			]}
		>
			<div style={{ paddingBottom: "20px", fontSize: "18px" }}>
				{i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.select")}
			</div>
			<CoreCard
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.mode")}
				useHeader
				headerFontSize={"20px"}
				headerPadding={2}
				style={{ marginBottom: "20px" }}
				icon={<ProctoringPlatform />}
			>
				<ModeOptions
					option={currProfile.proctoringOption}
					updateOption={updateProfileOption}
					verityInstituteSettings={verityInstituteSettings}
					isInstitutionProfile={true}
				/>
			</CoreCard>
			<CoreCard
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.procSetting")}
				useHeader
				headerFontSize={"20px"}
				headerPadding={2}
				icon={<ProctoringSettings />}
			>
				<ProctoringOptionsList
					option={currProfile.proctoringOption}
					updateOption={updateProfileOption}
					shouldShowOption={shouldShowOption}
					isOptionAllowed={isOptionAllowed}
					applications={applications}
					extensions={extensions}
					fetchChromeExtensionData={apiClient.verityOptions.fetchChromeExtensionData}
					allowedCategories={allowedCategories}
					horizontalOrientation
					instituteUuid={portalContext.instituteUuid}
					getInstituteSettings={apiClient.institute.getSettings}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			</CoreCard>
		</CoreModal>
	);
};

export const CreateProfileModal = ({
	editProfile,
	lastAddedId,
	setLastAddedId,
	verityInstituteSettings,
	instituteSettings,
	open,
	onClose,
}: {
	editProfile: (profile: ProctoringOptionProfileV2) => void;
	lastAddedId: number;
	setLastAddedId: (lastAddedId: number) => void;
	verityInstituteSettings: VerityInstituteSettings;
	instituteSettings: OptionRelatedInstituteSettings;
	open: boolean;
	onClose: () => void;
}) => {
	const { portalContext } = useContext(PortalControllerContext);
	const [currProfile, setCurrProfile] = React.useState<ProctoringOptionProfileV2>({
		proctoringOption: getDefaultProctoringOption(verityInstituteSettings),
		id: lastAddedId - 1,
		name: "",
		type: "institute",
	});

	const updateProfileOption = (proctoringOption: ProctoringOption) => {
		setCurrProfile({ ...currProfile, proctoringOption });
	};

	const proctoringOptions = currProfile.proctoringOption;
	const shouldShowOption = useCallback(
		(option: keyof ProctoringOption) =>
			shouldShowProctoringOption(option, proctoringOptions, verityInstituteSettings, instituteSettings),
		[proctoringOptions, verityInstituteSettings, instituteSettings],
	);
	const isOptionAllowed = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]) =>
			value === undefined
				? isAnyOnValueEnabledForProctoringOption(option, proctoringOptions, verityInstituteSettings, instituteSettings)
				: !getProctoringOptionDisabledDetails(
						option,
						value,
						proctoringOptions,
						verityInstituteSettings,
						instituteSettings,
					).disabled,
		[proctoringOptions, verityInstituteSettings, instituteSettings],
	);

	const getPartiallySupportedPlatforms = useCallback(
		<O extends keyof ProctoringOption>(option: O, value?: ProctoringOption[O]): Set<keyof ProctoringPlatforms> => {
			const optionInfo = PROCTORING_OPTION_INFO[option];

			const valuesToCheck =
				value !== undefined
					? [value]
					: optionInfo?.isList
						? [[""] as ProctoringOption[O]]
						: (optionInfo?.onValues ?? []);

			const supportedPlatforms = new Set<keyof ProctoringPlatforms>();
			if (!proctoringOptions) return new Set();
			const selectedPlatforms = (["isExtension", "isLockdown", "isMobile"] as (keyof ProctoringPlatforms)[]).filter(
				(platform) => proctoringOptions[platform],
			);

			selectedPlatforms.forEach((platform) => {
				const isSupported = valuesToCheck.some((v) => isOptionSupportedForPlatform(platform, option, v));
				if (isSupported) {
					supportedPlatforms.add(platform);
				}
			});

			// If all selected platforms are supported, return empty
			if (supportedPlatforms.size === selectedPlatforms.length) return new Set();
			return new Set(supportedPlatforms);
		},
		[proctoringOptions],
	);

	const allowedCategories = PROCTORING_OPTION_CATEGORIES.filter((category) =>
		getOptionsInCategory(category).some(shouldShowOption),
	);

	const [applications, setApplications] = useState<Application[]>([]);
	const [extensions, setExtensions] = useState<Extension[]>([]);

	useEffect(() => {
		async function fetchApplicationData() {
			const APPLICATION_LIST = await apiClient.verityOptions.getAllApplications();
			setApplications(APPLICATION_LIST);
		}
		async function fetchExtensionData() {
			const EXTENSION_LIST = await apiClient.verityOptions.getAllExtensions();
			setExtensions(EXTENSION_LIST);
		}

		void fetchApplicationData();
		void fetchExtensionData();
	}, []);

	return (
		<CoreModal
			open={open}
			onClose={onClose}
			title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.createProfile")}
			minWidth="min(80%, 1200px)"
			minHeight="min(90%, 900px)"
			widthFixed
			heightFixed
			padding="18px 24px 12px 24px"
			buttons={[
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.create"),
					color: "primary",
					onClick: () => {
						editProfile(currProfile);
						setLastAddedId(lastAddedId - 1);
						onClose();
					},
					disabled: currProfile.name === "",
				},
			]}
		>
			<div style={{ paddingBottom: "20px", fontSize: "18px" }}>
				{i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.chooseName")}
			</div>
			<CoreCard
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.profileName")}
				useHeader
				headerFontSize={"20px"}
				headerPadding={2}
				style={{ marginBottom: "20px" }}
				icon={<ProfileName />}
			>
				<CoreTextBox
					value={currProfile.name}
					onChange={(e) => setCurrProfile({ ...currProfile, name: e.target.value })}
					placeholder={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.enterName")}
					autoComplete="off"
					inputProps={{
						maxLength: 30,
					}}
					style={{ maxWidth: "100%", width: "400px" }}
				/>
			</CoreCard>
			<CoreCard
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.mode")}
				useHeader
				headerFontSize={"20px"}
				headerPadding={2}
				style={{ marginBottom: "20px" }}
				icon={<ProctoringPlatform />}
			>
				<ModeOptions
					option={currProfile.proctoringOption}
					updateOption={updateProfileOption}
					verityInstituteSettings={verityInstituteSettings}
					isInstitutionProfile={true}
				/>
			</CoreCard>
			<CoreCard
				title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.procSetting")}
				useHeader
				headerFontSize={"20px"}
				headerPadding={2}
				icon={<ProctoringSettings />}
			>
				<ProctoringOptionsList
					option={currProfile.proctoringOption}
					updateOption={updateProfileOption}
					shouldShowOption={shouldShowOption}
					isOptionAllowed={isOptionAllowed}
					applications={applications}
					extensions={extensions}
					fetchChromeExtensionData={apiClient.verityOptions.fetchChromeExtensionData}
					allowedCategories={allowedCategories}
					horizontalOrientation
					instituteUuid={portalContext.instituteUuid}
					getInstituteSettings={apiClient.institute.getSettings}
					getPartiallySupportedPlatforms={getPartiallySupportedPlatforms}
				/>
			</CoreCard>
		</CoreModal>
	);
};

export const DeleteProfileModal = ({
	profile,
	deleteProfile,
	open,
	onClose,
}: {
	profile: ProctoringOptionProfileV2;
	deleteProfile: (profile: ProctoringOptionProfileV2) => void;
	open: boolean;
	onClose: () => void;
}) => {
	return (
		<CoreModal
			title={i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.deleteProfile")}
			open={open}
			onClose={onClose}
			minWidth="min(60%, 550px)"
			widthFixed
			buttons={[
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.cancel"),
					color: "secondary",
					onClick: onClose,
				},
				{
					text: i18n.t("portal.components.settings.instituteGlobalProfile.profileModalsTSX.delete"),
					color: "primary",
					onClick: () => {
						deleteProfile(profile);
						onClose();
					},
				},
			]}
		>
			<Trans
				i18nKey="portal.components.settings.instituteGlobalProfile.profileModalsTSX.deleteText"
				values={{ profileName: profile.name }}
				components={{ b: <b /> }}
			/>
		</CoreModal>
	);
};
